import {
  Box,
  Button,
  Icon,
  ProgressBar,
  SearchField,
  Text,
  Tooltip,
  colors,
  useBreakpoint,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { AlertCircle } from '@factorialco/f0-react/icons/app'
import {
  ProjectionKind,
  usePerformanceReviewsDataTable,
} from 'generated/resources/datatable.graphql'
import { PerformanceProcessReviewsDataTableCycleFragment } from 'generated/resources/graphql'
import compact from 'lodash/compact'
import moment from 'moment'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { CycleTag } from 'modules/performance_cycles/components/CycleTag'

import { Actions } from './Actions'
import { archivedFilter } from './filters/archivedFilter'
import createdOnFilter from './filters/createdOnFilter'
import { cycleFilter } from './filters/cycleFilter'
import dueDateFilter from './filters/dueDateFilter'
import ownerFilter from './filters/ownerFilter'
import participationRateFilter from './filters/promotionRateFilter'
import revieweeFilter from './filters/revieweeFilter'
import startDateFilter from './filters/startDateFilter'
import { statusFilter } from './filters/statusFilter'
import { StatusTag } from './helpers/StatusTag'

interface Props {
  cycles: PerformanceProcessReviewsDataTableCycleFragment[]
  createProcess: () => void
  canCreateReview?: boolean
  canSeePerformanceAnalytics?: boolean
  hasGoalsConnectionFeature?: boolean
  actions?: React.ReactNode
}

const isParticipationRateLow = (
  currentRate: number,
  startDate: string,
  endDate: string,
  finalTarget = 0.8
): boolean => {
  if (currentRate == 1) {
    return false
  }
  const today = moment()
  const activeDays = today.diff(moment(startDate), 'days')
  const totalDays = moment(endDate).diff(moment(startDate), 'days')
  if (activeDays <= 0 || totalDays <= 0) {
    return false
  }
  const expectedRate = (finalTarget / totalDays) * activeDays
  return currentRate < expectedRate
}

const formatPercentage = (value: number): string => {
  return i18n.formatNumber(value, 'percent')
}

interface ParticipationAlertProps {
  label: string
}

const ParticipationAlert = ({ label }: ParticipationAlertProps) => {
  const [hover, setHover] = useToggle(false)

  return (
    <Tooltip.Alt attachment="bottom center" open={hover} label={label} toggle={setHover}>
      <AlertCircle width={24} color={colors.radicalRed1000} />
    </Tooltip.Alt>
  )
}

const ReviewsDataTable: React.FC<Props> = ({
  cycles,
  canCreateReview,
  canSeePerformanceAnalytics,
  createProcess,
  hasGoalsConnectionFeature,
  actions,
}) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const { isMobile } = useBreakpoint()

  const source = usePerformanceReviewsDataTable(
    compact([
      {
        identifier: 'name',
        title: i18n.t('performance_process.reviews.datatable.name'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ name }}',
        colSpan: 2,
        render: ({ name }) => {
          return (
            <Box minWidth="s120">
              <Text.Normal whiteSpace="nowrap">
                {name || i18n.t('performance_process.reviews.untitled')}
              </Text.Normal>
            </Box>
          )
        },
        sortBy: 'name',
      },
      {
        identifier: 'status',
        title: i18n.t('performance_process.reviews.datatable.status'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ status }}',
        render: ({ status, archived }) => {
          return (
            <Box width="s96">
              <StatusTag status={status} archived={archived} />
            </Box>
          )
        },
        sortBy: 'status',
      },
      {
        identifier: 'participation_rate',
        title: i18n.t('performance_process.reviews.datatable.participation_rate'),
        fixed: true,
        kind: ProjectionKind.Custom,
        hidden: isMobile || !canSeePerformanceAnalytics,
        template: '{{ participation_rate }}',
        render: ({
          endsAt,
          status,
          startsAt,
          evaluationsConnection,
          publishedEvaluationsConnection,
        }) => {
          const totalEvaluations = evaluationsConnection.totalCount
          const totalPublishedEvaluationsCount = publishedEvaluationsConnection.totalCount
          const currentParticipationRate =
            totalPublishedEvaluationsCount && totalEvaluations
              ? totalPublishedEvaluationsCount / totalEvaluations
              : 0
          const showParticipationAlert =
            isParticipationRateLow(currentParticipationRate, startsAt, endsAt) &&
            status !== 'finished'

          return totalEvaluations && totalPublishedEvaluationsCount ? (
            <Box flexDirection="row" gap="s6" alignItems="center">
              <Box width="s120">
                <ProgressBar
                  progressColor={showParticipationAlert ? 'radicalRed1000' : 'viridian1000'}
                  amount={totalPublishedEvaluationsCount}
                  total={totalEvaluations}
                />
              </Box>
              <Box width="s80" flexDirection="row" gap="s4" alignItems="center">
                <Text.Normal size="200">
                  {totalPublishedEvaluationsCount}/{totalEvaluations}
                </Text.Normal>
                {showParticipationAlert && (
                  <ParticipationAlert
                    label={i18n.t(
                      'performance_process.reviews.datatable.participation_rate_alert',
                      {
                        days_left: moment(endsAt).diff(moment(), 'days'),
                        participation_percentage: formatPercentage(currentParticipationRate),
                      }
                    )}
                  />
                )}
              </Box>
            </Box>
          ) : (
            <Box width="s120">-</Box>
          )
        },
      },
      {
        identifier: 'created_on',
        title: i18n.t('performance_process.reviews.datatable.created_on'),
        fixed: true,
        kind: ProjectionKind.Custom,
        hidden: isMobile,
        template: '{{ createdAt }}',
        render: ({ createdAt }) => {
          return (
            <Box width="s96">
              {createdAt ? <Text.Normal>{dateBackendDateTimeToUI(createdAt)}</Text.Normal> : '-'}
            </Box>
          )
        },
        sortBy: 'createdAt',
      },
      {
        identifier: 'starts_at',
        title: i18n.t('performance_process.reviews.datatable.start_date'),
        fixed: true,
        kind: ProjectionKind.Custom,
        hidden: isMobile,
        template: '{{ startsAt }}',
        render: ({ startsAt }) => {
          return (
            <Box width="s96">
              {startsAt ? <Text.Normal>{dateBackendDateTimeToUI(startsAt)}</Text.Normal> : '-'}
            </Box>
          )
        },
        sortBy: 'startsAt',
      },
      {
        identifier: 'deadline',
        title: i18n.t('performance_process.reviews.datatable.deadline'),
        fixed: true,
        kind: ProjectionKind.Custom,
        hidden: isMobile,
        template: '{{ deadline }}',
        render: ({ endsAt }) => {
          return (
            <Box width="s96">
              {endsAt ? <Text.Normal>{dateBackendDateTimeToUI(endsAt)}</Text.Normal> : '-'}
            </Box>
          )
        },
        sortBy: 'endsAt',
      },
      {
        identifier: 'cycle',
        title: i18n.t('performance_cycles.cycle_filter.label'),
        kind: ProjectionKind.Custom,
        template: '{{ cycle }}',
        hidden: !hasGoalsConnectionFeature,
        colSpan: 2,
        render: ({ performanceCycle }) =>
          performanceCycle ? (
            <CycleTag
              title={performanceCycle.title}
              startOn={performanceCycle.startOn}
              endOn={performanceCycle.endOn}
            />
          ) : (
            <Text.Normal>-</Text.Normal>
          ),
      },
      !isMobile && {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 100,
        fixed: true,
        sticky: true,
        render: (review) => {
          return <Actions review={review} canCreateReview={canCreateReview} />
        },
      },
    ]),
    {
      variables: { ids: [], search: debouncedSearch },
    }
  )

  // filter starts_at and deadline columns based on the breakpoint
  const EmptyState = (
    <>
      <Icon.Large icon="edit-file" color="grey600" />
      <Box flexDirection="column" gap="s8" textAlign="center">
        <Text size="300" weight="semibold" color="grey1000">
          {i18n.t('performance_process.reviews.datatable.empty_state.no_reviews_title')}
        </Text>
        {canCreateReview ? (
          <Box gap="s32" alignItems="center" textAlign="center">
            <Box>
              <Text.Normal color="grey800">
                {i18n.t('performance_process.reviews.datatable.empty_state.action_description')}
              </Text.Normal>
            </Box>
            <Button iconName="add" onClick={createProcess}>
              {i18n.t('performance_process.reviews.new_review')}
            </Button>
          </Box>
        ) : (
          <Text.Normal color="grey800">
            {i18n.t('performance_process.reviews.datatable.empty_state.general_description')}
          </Text.Normal>
        )}
      </Box>
    </>
  )

  const { navigation } = useNavigation()

  return (
    <DataTable.Provider
      source={source}
      filters={compact([
        ownerFilter(source),
        revieweeFilter(source),
        statusFilter(source),
        createdOnFilter(source),
        startDateFilter(source),
        dueDateFilter(source),
        participationRateFilter(source),
        archivedFilter(source),
        hasGoalsConnectionFeature && cycleFilter(source, cycles),
      ])}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
      }}
      sort={{
        path: 'status',
        direction: 'asc',
      }}
      rowTo={({ id }) =>
        isMobile
          ? navigation.performanceProcess.reviews.review.fullPath({ reviewId: id })
          : undefined
      }
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableDefaultActions
          actions={
            <Box flexDirection="row" gap="s8" alignItems="center">
              <SearchField
                placeholder={i18n.t('common.search_by')}
                search={search}
                onSearch={onSearch}
              />
              {actions}
            </Box>
          }
        />
        <DataTable.Table emptyState={EmptyState} autoWidth />
      </Box>
    </DataTable.Provider>
  )
}

export default ReviewsDataTable
